/**
 * Wire schemas for the Phase 1 field entities (planning, progress, issues,
 * site diary, media) and the project dashboard.
 *
 * Everything written to IndexedDB is parsed here first. The API renders
 * decimals as strings ("60.00"), so percentages are coerced to numbers.
 * Unknown fields are stripped; missing optional fields get safe defaults so a
 * newer server cannot break an older client.
 */
import { z } from 'zod';

const id = z.string();
const nullableId = z.string().nullable().default(null);
const date = z.string().nullable().default(null);
const text = z.string().default('');
const percent = z.coerce.number().min(0).max(100).catch(0);
const version = z.number().int().nullable().default(null);

// ---------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------

export const STAGE_STATUSES = ['not_started', 'in_progress', 'on_hold', 'completed'] as const;

export const stageSchema = z.object({
  id,
  project_id: id,
  name: z.string(),
  template_key: text,
  sequence: z.number().default(0),
  status: z.string().default('not_started'),
  planned_start: date,
  planned_end: date,
  actual_start: date,
  actual_end: date,
  responsible_id: nullableId,
  reported_percent: percent.default(0),
  verified_percent: percent.default(0),
  depends_on: z.array(z.string()).default([]),
  notes: text,
  budget: z.object({ amount_minor: z.number(), currency: z.string() }).nullable().optional(),
  version,
  updated_at: date,
});
export type Stage = z.infer<typeof stageSchema>;

export const MILESTONE_STATUSES = ['pending', 'achieved', 'missed'] as const;

export const milestoneSchema = z.object({
  id,
  project_id: id,
  stage_id: nullableId,
  name: z.string(),
  due_date: date,
  status: z.string().default('pending'),
  achieved_at: date,
  requires_owner_approval: z.boolean().default(false),
  version,
});
export type Milestone = z.infer<typeof milestoneSchema>;

export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
/** Fields a field user (`tasks.update`) may change; everything else is planning. */
export const TASK_FIELD_EDITABLE = ['status', 'blocked_reason', 'notes'] as const;
export const TASK_PLANNING_EDITABLE = [
  'title',
  'description',
  'stage_id',
  'milestone_id',
  'assignee_id',
  'due_date',
  'priority',
  // Module 4: when it starts, where, which crew, which BOQ item, what it waits on.
  'planned_start',
  'zone_id',
  'crew_id',
  'boq_lineage',
  'depends_on',
] as const;

export const checklistItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  done: z.boolean().default(false),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const taskSchema = z.object({
  id,
  project_id: id,
  stage_id: nullableId,
  milestone_id: nullableId,
  title: z.string(),
  description: text,
  assignee_id: nullableId,
  due_date: date,
  priority: z.string().default('normal'),
  status: z.string().default('todo'),
  blocked_reason: text,
  notes: text,
  checklist: z.array(checklistItemSchema).catch([]).default([]),
  completed_at: date,
  planned_start: date,
  zone_id: nullableId,
  crew_id: nullableId,
  boq_lineage: nullableId,
  /** Finish-to-start predecessors (task ids). */
  depends_on: z.array(z.string()).catch([]).default([]),
  version,
  updated_at: date,
});
export type Task = z.infer<typeof taskSchema>;

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export const progressUpdateSchema = z.object({
  id,
  project_id: id,
  stage_id: id,
  task_id: nullableId,
  author_id: nullableId,
  /** Present on local claims (what the device believed was current). */
  base_percent: z.coerce.number().nullable().optional(),
  previous_percent: z.coerce.number().nullable().default(null),
  percent: percent,
  narrative: text,
  captured_at_device: date,
  status: z.string().default('submitted'),
  reviewed_at: date,
  review_note: text,
  created_at: date,
  version,
});
export type ProgressUpdate = z.infer<typeof progressUpdateSchema>;

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

export const ISSUE_KINDS = ['blocker', 'defect', 'safety', 'quality', 'rfi', 'other'] as const;
export const ISSUE_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export const ISSUE_STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const;
export const ISSUE_EDITABLE = [
  'title',
  'kind',
  'severity',
  'description',
  'location',
  'stage_id',
  'assignee_id',
  'due_date',
  'client_visible',
] as const;

export const issueSchema = z.object({
  id,
  project_id: id,
  stage_id: nullableId,
  task_id: nullableId,
  title: z.string(),
  kind: z.string().default('other'),
  severity: z.string().default('medium'),
  description: text,
  location: text,
  assignee_id: nullableId,
  due_date: date,
  status: z.string().default('open'),
  reported_by_id: nullableId,
  reported_by_party: text,
  resolution_notes: text,
  resolved_by_id: z.string().nullable().default(null),
  resolved_at: date,
  closed_at: date,
  client_visible: z.boolean().default(true),
  created_at: date,
  version,
});
export type Issue = z.infer<typeof issueSchema>;

// ---------------------------------------------------------------------------
// Site diary
// ---------------------------------------------------------------------------

export const DIARY_NARRATIVE_FIELDS = [
  'weather',
  'work_performed',
  'materials_received',
  'equipment_used',
  'visitors',
  'safety_notes',
  'delays',
  'instructions',
  'notes',
] as const;
export type DiaryNarrativeField = (typeof DIARY_NARRATIVE_FIELDS)[number];
export const DIARY_EDITABLE = [...DIARY_NARRATIVE_FIELDS, 'workers_on_site'] as const;
export type DiaryEditableField = (typeof DIARY_EDITABLE)[number];

export const workerRowSchema = z.object({
  trade: z.string(),
  count: z.coerce.number().int().min(0).catch(0),
});
export type WorkerRow = z.infer<typeof workerRowSchema>;

export const diaryEntrySchema = z.object({
  id,
  project_id: id,
  entry_date: z.string(),
  status: z.string().default('draft'),
  author_id: nullableId,
  weather: text,
  work_performed: text,
  materials_received: text,
  equipment_used: text,
  visitors: text,
  safety_notes: text,
  delays: text,
  instructions: text,
  notes: text,
  workers_on_site: z.array(workerRowSchema).catch([]).default([]),
  submitted_at: date,
  approved_at: date,
  version,
  updated_at: date,
});
export type DiaryEntry = z.infer<typeof diaryEntrySchema>;

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export const MEDIA_ENTITY_TYPES = [
  'diary_entry',
  'project',
  'progress_update',
  'issue',
  'task',
  'attendance_event',
  // Phase 3: receipts on expenses, documents on variations.
  'expense',
  'variation',
  // Phase 4: photos and the receiver's signature on deliveries, invoice photos.
  'delivery',
  'supplier_invoice',
  // The photo or PDF of a receipt/invoice being read (moves to the record on commit).
  'document_extraction',
  // Module 3: quality and safety evidence (inspection signatures, snag photos…).
  'inspection',
  'inspection_result',
  'ncr',
  'rectification',
  'snag',
  'floor_plan',
  'test_record',
  'incident',
  'toolbox_talk',
  // Module 5: a photo of what failed a pre-use check; a submittal's samples and data sheets.
  'equipment_check',
  'material_submittal',
  // Module 6: an RFI's sketches and photos, the answer's attachments, a visit's observations.
  'rfi',
  'rfi_response',
  'observation',
  // Module 7: valuation evidence, a measured row's photo, a claim's or notice's
  // documents, a payment's remittance advice.
  'valuation',
  'measurement',
  'claim',
  'notice',
  'payment',
  // Module 8: a subcontractor's daily report photos (never shared with the owner side).
  'sub_daily_report',
  // Module 9: a change request's photos, a selection option's product photo, a warranty defect.
  'change_request',
  'selection_option',
  'warranty_claim',
  // Module 11: a message's photos and documents (they follow its conversation).
  'message',
  // A subcontractor's own purchase receipts (party-private: its own party only).
  'sub_purchase',
] as const;
export type MediaEntityType = (typeof MEDIA_ENTITY_TYPES)[number];
export type MediaKind = 'photo' | 'video' | 'document' | 'signature';

export const mediaAssetSchema = z.object({
  id,
  project_id: id,
  kind: z.string().default('photo'),
  /** pending → uploaded → available | rejected */
  status: z.string().default('pending'),
  entity_type: z.string(),
  entity_id: z.string(),
  content_type: z.string().default(''),
  size_bytes: z.number().default(0),
  sha256: text,
  caption: text,
  captured_at_device: date,
  client_visible: z.boolean().default(false),
  has_derivatives: z.boolean().default(false),
  version,
});
export type MediaAsset = z.infer<typeof mediaAssetSchema>;

export const uploadInitResponseSchema = z.object({
  asset: mediaAssetSchema,
  upload: z.object({
    method: z.string().default('PUT'),
    url: z.string(),
    headers: z.record(z.string(), z.string()).default({}),
  }),
});
export type UploadInitResponse = z.infer<typeof uploadInitResponseSchema>;

// ---------------------------------------------------------------------------
// Members (synced as project_member)
// ---------------------------------------------------------------------------

export const memberSchema = z.object({
  id,
  project_id: id,
  user_id: nullableId,
  name: text,
  avatar_url: z.string().nullable().optional(),
  role: text,
  /** The party (organisation's side) this membership is held through. */
  party_id: nullableId,
  party_role: text,
  organization_name: text,
  /** A person's own organisation (individual owner, sole trader): never shown as a company. */
  organization_is_personal: z.boolean().default(false),
  status: z.string().default('active'),
});
export type Member = z.infer<typeof memberSchema>;

// ---------------------------------------------------------------------------
// Dashboard (online; cached for offline viewing)
// ---------------------------------------------------------------------------

export const dashboardSchema = z.object({
  today: z.string(),
  project: z
    .object({
      name: z.string().default(''),
      timezone: z.string().default('Africa/Nairobi'),
      target_completion_date: date,
    })
    .partial()
    .default({}),
  progress: z.object({ reported_percent: percent, verified_percent: percent }),
  stages: z
    .array(
      z.object({
        id,
        name: z.string(),
        status: z.string().default('not_started'),
        reported_percent: percent,
        verified_percent: percent,
        planned_end: date,
        behind: z.boolean().default(false),
      }),
    )
    .default([]),
  schedule: z
    .object({ target_completion_date: date, days_remaining: z.number().nullable().default(null) })
    .default({ target_completion_date: null, days_remaining: null }),
  upcoming_milestones: z
    .array(z.object({ id, name: z.string(), due_date: date, stage_id: nullableId }))
    .default([]),
  issues: z
    .object({
      open: z.number().default(0),
      blockers: z.number().default(0),
      critical: z.number().default(0),
      safety: z.number().default(0),
    })
    .optional(),
  diary: z
    .object({
      today_status: z.string().nullable().default(null),
      today_entry_id: nullableId,
      last_entry_date: date,
    })
    .optional(),
  recent_media: z.array(mediaAssetSchema).optional(),
  /** Client-safe headcount; names and pay stay contractor-side. */
  workforce: z
    .object({
      engaged: z.number().default(0),
      checked_in_today: z.number().default(0),
      on_site_now: z.number().default(0),
    })
    .optional(),
  tasks: z
    .object({
      due_today: z.number().default(0),
      overdue: z.number().default(0),
      blocked: z.number().default(0),
      due_this_week: z.number().default(0),
    })
    .optional(),
  awaiting_verification: z.number().optional(),
  /** Module 3: what this side may see (the owner side: shared records only). */
  quality: z
    .object({
      open_ncrs: z.number().default(0),
      open_snags: z.number().default(0),
      inspections_pending: z.number().default(0),
      inspections_failed: z.number().default(0),
    })
    .optional(),
  /** Counts only, for both sides: never names. */
  safety: z
    .object({
      open_incidents: z.number().default(0),
      incidents_30d: z.number().default(0),
      days_without_lost_time: z.number().nullable().default(null),
    })
    .optional(),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

// ---------------------------------------------------------------------------
// Notifications and invitations
// ---------------------------------------------------------------------------

export const notificationSchema = z.object({
  id,
  event: z.string().default(''),
  /** The centre's filter: approvals | mentions | messages | site | money | system. */
  group: z.string().default('system'),
  title: z.string().default(''),
  body: text,
  project_id: nullableId,
  link: z
    .object({
      to: z.string().optional(),
      params: z.record(z.string(), z.string()).optional(),
      search: z.record(z.string(), z.unknown()).optional(),
    })
    .catch({})
    .default({}),
  read_at: date,
  created_at: z.string(),
});
export type AppNotification = z.infer<typeof notificationSchema>;
